import { useState, useCallback, useEffect } from 'react';
import { IngestPage } from '@/pages/IngestPage';
import { PreviewPage } from '@/pages/PreviewPage';

type AppState = { page: 'ingest' } | { page: 'preview'; runId: string } | { page: 'history'; folderName: string };

function parseHash(): AppState {
  const hash = window.location.hash;
  const runMatch = hash.match(/^#\/run\/(.+)$/);
  if (runMatch?.[1]) return { page: 'preview', runId: runMatch[1] };
  const historyMatch = hash.match(/^#\/history\/(.+)$/);
  if (historyMatch?.[1]) return { page: 'history', folderName: decodeURIComponent(historyMatch[1]) };
  return { page: 'ingest' };
}

export function App() {
  const [state, setState] = useState<AppState>(parseHash);

  useEffect(() => {
    const onHashChange = () => setState(parseHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const handleRunStarted = useCallback((runId: string) => {
    window.location.hash = `#/run/${runId}`;
    setState({ page: 'preview', runId });
  }, []);

  const handleViewHistory = useCallback((folderName: string) => {
    window.location.hash = `#/history/${encodeURIComponent(folderName)}`;
    setState({ page: 'history', folderName });
  }, []);

  const handleBack = useCallback(() => {
    window.location.hash = '';
    setState({ page: 'ingest' });
  }, []);

  if (state.page === 'preview') {
    return (
      <PreviewPage
        runId={state.runId}
        onBack={handleBack}
        onRunStarted={handleRunStarted}
      />
    );
  }

  if (state.page === 'history') {
    return (
      <PreviewPage
        folderName={state.folderName}
        onBack={handleBack}
        onRunStarted={handleRunStarted}
      />
    );
  }

  return (
    <IngestPage
      onRunStarted={handleRunStarted}
      onViewHistory={handleViewHistory}
    />
  );
}
